/**
 * Virginia Home Care Organization Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: 12VAC5-381 (Regulations for the Licensure of Home Care Organizations)
 * - Criminal Background Checks: Code of Virginia § 32.1-162.9:1
 * - Adult Protective Services: Code of Virginia § 63.2-1606
 * - Statutory Authority: Code of Virginia §§ 32.1-12, 32.1-162.12
 * 
 * REGULATORY BODY:
 * Virginia Department of Health (VDH)
 * Office of Licensure and Certification (OLC)
 * 9960 Mayland Drive, Suite 401, Richmond, VA 23233
 * Phone: (804) 367-2106
 * Website: https://www.vdh.virginia.gov/licensure-and-certification/home-care-organizations/
 * 
 * REGULATION STRUCTURE (12VAC5-381):
 * Part I - Definitions and General Information (§10-140)
 *   - §10: Definitions
 *   - §20: License
 *   - §40: License application; initial and renewal
 *   - §60: Changes to or reissue of a license
 *   - §80: On-site inspections
 *   - §110: Criminal records checks
 *   - §130: Revocation or suspension of a license
 * 
 * Part II - Administrative Services (§150-290)
 *   - §150: Management and administration
 *   - §160: Governing body
 *   - §170: Administrator
 *   - §180: Written policies and procedures
 *   - §190: Financial controls
 *   - §200: Personnel practices
 *   - §210: Indemnity coverage
 *   - §220: Contract services
 *   - §230: Client rights
 *   - §240: Handling complaints received from clients
 *   - §250: Quality improvement
 *   - §260: Infection control
 *   - §280: Client record system
 *   - §290: Home attendants (qualifications)
 * 
 * Part III - Skilled Services (§300-340)
 *   - §300: Skilled services
 *   - §310: Nursing services
 *   - §330: Home attendants assisting with skilled services
 * 
 * Part V - Personal Care Services (§360)
 *   - §360: Personal care services
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - Employee Record Retention: 3 years after termination per 12VAC5-381-200(Q)
 * - Client Record Retention: Per organization policy per 12VAC5-381-280(A)
 * - Annual In-Service Training: 12 hours per 12VAC5-381-330(D) and 12VAC5-381-360(G)
 * - Supervision Frequency: Not to exceed 90 days per 12VAC5-381-360(E)
 * - Criminal Background Check: Required per 12VAC5-381-110 and Code of Virginia § 32.1-162.9:1
 * - Home Attendant Qualifications: Multiple pathways per 12VAC5-381-290
 * - RN/LPN Availability: Required during all personal care service hours per 12VAC5-381-360(F)
 * 
 * Last Verified: December 2024 against current Virginia Administrative Code
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const VIRGINIA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'VA-A1',
    itemId: 'A1',
    stateCode: 'VA',
    title: 'VDH Home Care Organization License Status',
    description: 'Virginia requires all home care organizations to be licensed by VDH per 12VAC5-381-20',
    severity: 'high',
    regulationCode: '12VAC5-381-20',
    regulationName: 'License',
    agencyName: 'Virginia Department of Health - Office of Licensure and Certification',
    agencyAbbr: 'VDH-OLC',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov/licensure-and-certification/home-care-organizations/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Organization license from VDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-20(A): A license to operate a home care organization is issued to a person. Operating without a license is a Class 6 felony per 12VAC5-381-20(F).',
      },
      {
        id: 'q2',
        question: 'What is your HCO license expiration date?',
        inputType: 'date',
        helpText: 'Found on your VDH license certificate. Licenses are renewed per 12VAC5-381-40.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your VDH Home Care Organization license is current and active per 12VAC5-381-20.',
      atRisk: 'Your HCO license expires within 120 days. Submit renewal application per 12VAC5-381-40.',
      nonCompliant: 'Operating without a valid VDH license is a Class 6 felony per 12VAC5-381-20(F) and Code of Virginia § 32.1-162.10. Contact VDH-OLC immediately at (804) 367-2106.',
      unknown: 'Verify your HCO license status with VDH-OLC at (804) 367-2106.',
    },
    stateNotes: [
      'Operating without license is Class 6 felony per 12VAC5-381-20(F)',
      'License not transferable or assignable per 12VAC5-381-20(E)',
      'Branch offices must be listed on license per 12VAC5-381-20(C)',
      'Complete application 60 days before planned opening per 12VAC5-381-40(D)',
    ],
  },

  {
    id: 'VA-A2',
    itemId: 'A2',
    stateCode: 'VA',
    title: 'Governing Body Requirements (12VAC5-381-160)',
    description: 'Virginia requires an organized governing body with specific responsibilities',
    severity: 'high',
    regulationCode: '12VAC5-381-160',
    regulationName: 'Governing Body',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an organized governing body that fulfills required responsibilities?',
        inputType: 'checklist',
        options: [
          { value: 'legal_authority', label: 'Full legal authority and responsibility for organization operation - 12VAC5-381-160(A)' },
          { value: 'bylaws', label: 'Adopts bylaws or establishes policies - 12VAC5-381-160(B)' },
          { value: 'policies_review', label: 'Reviews and approves policies and procedures - 12VAC5-381-160(C)' },
          { value: 'admin_appoint', label: 'Appoints the administrator - 12VAC5-381-160(D)' },
          { value: 'budget', label: 'Approves annual budget and capital expenditures - 12VAC5-381-160(E)' },
          { value: 'qa_review', label: 'Reviews quality improvement program findings - 12VAC5-381-160(F)' },
          { value: 'legal_compliance', label: 'Ensures compliance with all applicable laws - 12VAC5-381-160(G)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-160: The governing body shall have these responsibilities.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets 12VAC5-381-160 requirements.',
      atRisk: 'Governing body may be missing some required responsibilities.',
      nonCompliant: 'Governing body structure is inadequate per 12VAC5-381-160.',
      unknown: 'Review governing body requirements against 12VAC5-381-160.',
    },
    stateNotes: [
      'Governing body must include Nursing Director (RN), Administrator, Financial Manager',
      'Meeting minutes must be signed and dated by attendees',
      'Evidence of policy review and administrator appointment required',
    ],
  },

  {
    id: 'VA-A3',
    itemId: 'A3',
    stateCode: 'VA',
    title: 'Administrator Qualifications (12VAC5-381-170)',
    description: 'Virginia requires a qualified administrator responsible for day-to-day operations',
    severity: 'high',
    regulationCode: '12VAC5-381-170',
    regulationName: 'Administrator',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet Virginia qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'designated', label: 'Administrator designated by governing body - 12VAC5-381-170(A)' },
          { value: 'responsible', label: 'Responsible for day-to-day management - 12VAC5-381-170(B)' },
          { value: 'designee', label: 'Qualified individual designated to act in absence - 12VAC5-381-170(C)' },
          { value: 'available', label: 'Administrator or designee available during operating hours - 12VAC5-381-170(D)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-170: Administrator requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets 12VAC5-381-170 requirements.',
      atRisk: 'Administrator qualifications may not fully meet requirements.',
      nonCompliant: 'Administrator does not meet 12VAC5-381-170 requirements.',
      unknown: 'Review administrator qualifications.',
    },
    stateNotes: [
      'Administrator designated by governing body per 12VAC5-381-170(A)',
      'Designee must be qualified to act in absence per 12VAC5-381-170(C)',
      'Administrator or designee available during operating hours per 12VAC5-381-170(D)',
    ],
  },

  {
    id: 'VA-A4',
    itemId: 'A4',
    stateCode: 'VA',
    title: 'Written Policies and Procedures (12VAC5-381-180)',
    description: 'Virginia requires comprehensive written policies and procedures',
    severity: 'high',
    regulationCode: '12VAC5-381-180',
    regulationName: 'Written Policies and Procedures',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which Virginia-required policies does your agency have documented?',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Scope and limitation of services - 12VAC5-381-180(A)(1)' },
          { value: 'service_area', label: 'Delineation of service area - 12VAC5-381-180(A)(2)' },
          { value: 'assessment', label: 'Client assessment process - 12VAC5-381-180(A)(3)' },
          { value: 'care_plan', label: 'Development and review of plan of care - 12VAC5-381-180(A)(4)' },
          { value: 'supervision', label: 'Supervision of services - 12VAC5-381-180(A)(5)' },
          { value: 'coordination', label: 'Coordination of services - 12VAC5-381-180(A)(6)' },
          { value: 'emergency', label: 'Emergency preparedness and response - 12VAC5-381-180(A)(7)' },
          { value: 'reporting', label: 'Reporting unusual incidents - 12VAC5-381-180(A)(8)' },
          { value: 'abuse', label: 'Abuse and neglect reporting - 12VAC5-381-180(A)(9)' },
          { value: 'drug_free', label: 'Drug-free workplace - 12VAC5-381-180(A)(10)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-180: Organization shall develop and implement written policies.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Written policies meet 12VAC5-381-180 requirements.',
      atRisk: 'Some required policies may be missing.',
      nonCompliant: 'Written policies are inadequate per 12VAC5-381-180.',
      unknown: 'Review policies against 12VAC5-381-180 requirements.',
    },
    stateNotes: [
      'Policies must address drug-free workplace per 12VAC5-381-180(A)(10)',
      'Abuse/neglect reporting policy required per 12VAC5-381-180(A)(9)',
      'Emergency preparedness required per 12VAC5-381-180(A)(7)',
    ],
  },

  {
    id: 'VA-A5',
    itemId: 'A5',
    stateCode: 'VA',
    title: 'Quality Improvement Program (12VAC5-381-250)',
    description: 'Virginia requires an ongoing quality improvement program',
    severity: 'high',
    regulationCode: '12VAC5-381-250',
    regulationName: 'Quality Improvement',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your quality improvement program include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'ongoing', label: 'Ongoing quality improvement program - 12VAC5-381-250(A)' },
          { value: 'written_plan', label: 'Written quality improvement plan - 12VAC5-381-250(B)' },
          { value: 'clinical_review', label: 'Clinical record review - 12VAC5-381-250(C)' },
          { value: 'client_satisfaction', label: 'Client satisfaction monitoring - 12VAC5-381-250(D)' },
          { value: 'corrective_action', label: 'Corrective action procedures - 12VAC5-381-250(E)' },
          { value: 'governing_review', label: 'Governing body reviews findings - 12VAC5-381-160(F)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-250: Quality improvement requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets 12VAC5-381-250 requirements.',
      atRisk: 'Quality improvement program may be incomplete.',
      nonCompliant: 'Quality improvement program is inadequate per 12VAC5-381-250.',
      unknown: 'Review quality improvement program.',
    },
    stateNotes: [
      'Written QI plan required per 12VAC5-381-250(B)',
      'Governing body must review findings per 12VAC5-381-160(F)',
      'Corrective action procedures required per 12VAC5-381-250(E)',
    ],
  },

  {
    id: 'VA-A6',
    itemId: 'A6',
    stateCode: 'VA',
    title: 'Indemnity Coverage (12VAC5-381-210)',
    description: 'Virginia requires liability insurance and surety bond',
    severity: 'high',
    regulationCode: '12VAC5-381-210',
    regulationName: 'Indemnity Coverage',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your HCO maintain?',
        inputType: 'checklist',
        options: [
          { value: 'prof_liability', label: 'Professional liability insurance ($2.5M per Virginia requirement)' },
          { value: 'general_liability', label: 'General liability insurance' },
          { value: 'workers_comp', label: 'Workers compensation insurance' },
          { value: 'surety_bond', label: 'Surety bond or third-party crime insurance - 12VAC5-381-210(B)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-210: Indemnity coverage requirements.',
      },
      {
        id: 'q2',
        question: 'Is proof of current insurance coverage readily available for OLC inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 12VAC5-381-210(D): Proof of coverage available for review.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets 12VAC5-381-210 requirements.',
      atRisk: 'Insurance coverage may be incomplete. Review requirements.',
      nonCompliant: 'Insurance coverage does not meet 12VAC5-381-210 requirements.',
      unknown: 'Verify insurance coverage.',
    },
    stateNotes: [
      'Professional liability $2.5M per occurrence required',
      'Surety bond or third-party crime insurance per 12VAC5-381-210(B)',
      'Proof of coverage available for review per 12VAC5-381-210(D)',
    ],
  },

  {
    id: 'VA-A7',
    itemId: 'A7',
    stateCode: 'VA',
    title: 'Client Rights (12VAC5-381-230)',
    description: 'Virginia requires written client rights policies',
    severity: 'high',
    regulationCode: '12VAC5-381-230',
    regulationName: 'Client Rights',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your client rights policy address required elements?',
        inputType: 'checklist',
        options: [
          { value: 'written_rights', label: 'Written statement of client rights - 12VAC5-381-230(A)' },
          { value: 'provided_client', label: 'Rights provided to client/representative - 12VAC5-381-230(B)' },
          { value: 'informed_services', label: 'Right to be informed of services - 12VAC5-381-230(C)(1)' },
          { value: 'participate', label: 'Right to participate in care planning - 12VAC5-381-230(C)(2)' },
          { value: 'refuse', label: 'Right to refuse services - 12VAC5-381-230(C)(3)' },
          { value: 'confidentiality', label: 'Right to confidentiality - 12VAC5-381-230(C)(4)' },
          { value: 'grievance', label: 'Right to voice grievances - 12VAC5-381-230(C)(5)' },
          { value: 'contact_info', label: 'VDH contact information provided - 12VAC5-381-230(D)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-230: Client rights requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights policy meets 12VAC5-381-230 requirements.',
      atRisk: 'Client rights policy may be incomplete.',
      nonCompliant: 'Client rights policy is inadequate per 12VAC5-381-230.',
      unknown: 'Review client rights policy.',
    },
    stateNotes: [
      'Written statement of rights required per 12VAC5-381-230(A)',
      'VDH contact information must be provided per 12VAC5-381-230(D)',
      'Rights provided at time of acceptance per 12VAC5-381-230(B)',
    ],
  },

  {
    id: 'VA-A8',
    itemId: 'A8',
    stateCode: 'VA',
    title: 'Complaint Handling (12VAC5-381-240)',
    description: 'Virginia requires documented complaint handling procedures',
    severity: 'medium',
    regulationCode: '12VAC5-381-240',
    regulationName: 'Handling Complaints Received from Clients',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your complaint handling process include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'written_procedure', label: 'Written complaint procedure - 12VAC5-381-240(A)' },
          { value: 'investigation', label: 'Investigation of complaints - 12VAC5-381-240(B)' },
          { value: 'resolution', label: 'Resolution and response to complainant - 12VAC5-381-240(C)' },
          { value: 'documentation', label: 'Documentation of complaints and resolution - 12VAC5-381-240(D)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-240: Complaint handling requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint handling meets 12VAC5-381-240 requirements.',
      atRisk: 'Complaint handling may be incomplete.',
      nonCompliant: 'Complaint handling is inadequate per 12VAC5-381-240.',
      unknown: 'Review complaint handling procedures.',
    },
    stateNotes: [
      'Written complaint procedure required per 12VAC5-381-240(A)',
      'Documentation of complaints and resolution per 12VAC5-381-240(D)',
    ],
  },

  // ============================================
  // SECTION B: CRIMINAL BACKGROUND CHECKS
  // ============================================
  {
    id: 'VA-B1',
    itemId: 'B1',
    stateCode: 'VA',
    title: 'Criminal Records Check (12VAC5-381-110)',
    description: 'Virginia requires criminal background checks per Code of Virginia § 32.1-162.9:1',
    severity: 'high',
    regulationCode: '12VAC5-381-110, Code of Virginia § 32.1-162.9:1',
    regulationName: 'Criminal Records Checks',
    agencyName: 'Virginia State Police',
    agencyAbbr: 'VSP',
    agencyPhone: '(804) 674-2000',
    agencyUrl: 'https://www.vsp.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'For ALL employees, which background checks do you complete?',
        inputType: 'checklist',
        options: [
          { value: 'sworn_statement', label: 'Sworn disclosure statement - 12VAC5-381-110(A)' },
          { value: 'vsp_check', label: 'Virginia State Police criminal history check - Code of VA § 32.1-162.9:1' },
          { value: 'sex_offender', label: 'Sex Offender Registry check - Code of VA § 32.1-162.9:1' },
          { value: 'cps_registry', label: 'Child Protective Services Central Registry check' },
          { value: 'oig', label: 'OIG/SAM exclusion list verification' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-110 and Code of Virginia § 32.1-162.9:1: Required background checks.',
      },
      {
        id: 'q2',
        question: 'Do employees begin working with clients BEFORE background check clearance?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-110(C): No employee permitted to work in position involving direct client contact until results received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before clearance (direct violation of 12VAC5-381-110(C))
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 3+ checks but not all required
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check process meets 12VAC5-381-110 and Code of Virginia § 32.1-162.9:1 requirements.',
      atRisk: 'Some required background checks are missing. Per 12VAC5-381-110, ensure all checks are completed.',
      nonCompliant: 'VIOLATION: Employees are working before background clearance OR required checks are missing. Per 12VAC5-381-110(C), NO employee may work in direct client contact until results received.',
      unknown: 'Audit background check compliance.',
    },
    stateNotes: [
      'Sworn disclosure statement required per 12VAC5-381-110(A)',
      'VSP criminal history check per Code of Virginia § 32.1-162.9:1',
      'No direct client contact until results received per 12VAC5-381-110(C)',
      'Barrier crimes prohibit employment per Code of Virginia § 32.1-162.9:1',
    ],
  },

  {
    id: 'VA-B2',
    itemId: 'B2',
    stateCode: 'VA',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Virginia requires OIG and SAM exclusion list verification for Medicaid providers',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'Exclusion List Verification',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often do you verify ALL employees against OIG and SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly verification for all employees' },
          { value: 'hire', label: 'At hire only' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'CMS requires monthly exclusion list checks for Medicaid providers.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list verification is conducted monthly.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG/SAM exclusion list verification. This creates significant Medicaid compliance risk.',
      unknown: 'Implement OIG/SAM exclusion list verification.',
    },
    stateNotes: [
      'Monthly verification required for Medicaid providers',
      'Check OIG LEIE at exclusions.oig.hhs.gov',
      'Check SAM.gov for federal exclusions',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL PRACTICES
  // ============================================
  {
    id: 'VA-C1',
    itemId: 'C1',
    stateCode: 'VA',
    title: 'Personnel Practices (12VAC5-381-200)',
    description: 'Virginia requires comprehensive personnel practices and documentation',
    severity: 'high',
    regulationCode: '12VAC5-381-200',
    regulationName: 'Personnel Practices',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which personnel practice elements do you have in place?',
        inputType: 'checklist',
        options: [
          { value: 'staffing_plan', label: 'Staffing plan for qualified staff - 12VAC5-381-200(B)' },
          { value: 'credentials', label: 'Mechanism to verify professional credentials - 12VAC5-381-200(D)' },
          { value: 'job_descriptions', label: 'Written job descriptions with qualifications - 12VAC5-381-200(G)' },
          { value: 'orientation', label: 'Orientation for employees and contractors - 12VAC5-381-200(I)' },
          { value: 'evaluations', label: 'Performance evaluation policy - 12VAC5-381-200(J)' },
          { value: 'staff_development', label: 'Staff development activities - 12VAC5-381-200(L)' },
          { value: 'identification', label: 'Staff identification (nametag/uniform) - 12VAC5-381-200(M)' },
          { value: 'confidentiality', label: 'Personnel file confidentiality - 12VAC5-381-200(N)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-200: Personnel practices requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel practices meet 12VAC5-381-200 requirements.',
      atRisk: 'Some personnel practice elements may be missing.',
      nonCompliant: 'Personnel practices are inadequate per 12VAC5-381-200.',
      unknown: 'Review personnel practices.',
    },
    stateNotes: [
      'Written job descriptions required per 12VAC5-381-200(G)',
      'Orientation required per 12VAC5-381-200(I)',
      'Performance evaluations required per 12VAC5-381-200(J)',
      'Staff identification required per 12VAC5-381-200(M)',
    ],
  },

  {
    id: 'VA-C2',
    itemId: 'C2',
    stateCode: 'VA',
    title: 'Personnel Record Requirements (12VAC5-381-200(O))',
    description: 'Virginia requires specific documents in employee personnel files',
    severity: 'high',
    regulationCode: '12VAC5-381-200(O)',
    regulationName: 'Personnel Records',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application - 12VAC5-381-200(O)(1)' },
          { value: 'verification', label: 'Verification of credentials/licenses - 12VAC5-381-200(O)(2)' },
          { value: 'job_description', label: 'Job description - 12VAC5-381-200(O)(3)' },
          { value: 'background', label: 'Criminal background check results - 12VAC5-381-200(O)(4)' },
          { value: 'references', label: 'Reference checks/employment verification - 12VAC5-381-200(O)(5)' },
          { value: 'orientation', label: 'Orientation documentation - 12VAC5-381-200(O)(6)' },
          { value: 'training', label: 'Training records - 12VAC5-381-200(O)(7)' },
          { value: 'evaluations', label: 'Performance evaluations - 12VAC5-381-200(O)(8)' },
          { value: 'drug_test', label: 'Drug testing results (if positive) - 12VAC5-381-200(P)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-200(O): Required personnel record contents.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel records contain all required documentation per 12VAC5-381-200(O).',
      atRisk: 'Some personnel files are missing required documents.',
      nonCompliant: 'Personnel files are incomplete per 12VAC5-381-200(O).',
      unknown: 'Audit personnel files.',
    },
    stateNotes: [
      'Records retained 3 years after termination per 12VAC5-381-200(Q)',
      'Health-related information maintained separately per 12VAC5-381-200(S)',
      'Positive drug tests reported to licensing boards per 12VAC5-381-200(P)',
    ],
  },

  {
    id: 'VA-C3',
    itemId: 'C3',
    stateCode: 'VA',
    title: 'Employee Record Retention (12VAC5-381-200(Q))',
    description: 'Virginia requires 3-year retention of employee records after termination',
    severity: 'medium',
    regulationCode: '12VAC5-381-200(Q)',
    regulationName: 'Personnel Record Retention',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain employee personnel records after termination?',
        inputType: 'select',
        options: [
          { value: '3plus', label: '3 years or longer (meets VA requirement)' },
          { value: '1to2', label: '1-2 years' },
          { value: 'less1', label: 'Less than 1 year' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-200(Q): Personnel records retained minimum 3 years after termination.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '3plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '1to2', result: 'AT_RISK' },
      { condition: 'includes', value: ['less1', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee record retention meets 3-year requirement per 12VAC5-381-200(Q).',
      atRisk: 'Virginia requires 3-year retention after termination. Update your policy.',
      nonCompliant: 'Record retention does not meet Virginia 3-year requirement per 12VAC5-381-200(Q).',
      unknown: 'Review record retention policy.',
    },
    stateNotes: [
      'Minimum 3 years after termination per 12VAC5-381-200(Q)',
      'Records safeguarded against loss and unauthorized use per 12VAC5-381-200(R)',
    ],
  },

  // ============================================
  // SECTION D: HOME ATTENDANT QUALIFICATIONS
  // ============================================
  {
    id: 'VA-D1',
    itemId: 'D1',
    stateCode: 'VA',
    title: 'Home Attendant Qualifications (12VAC5-381-290)',
    description: 'Virginia requires home attendants to meet specific qualification pathways',
    severity: 'high',
    regulationCode: '12VAC5-381-290',
    regulationName: 'Home Attendants',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home attendants meet at least one Virginia qualification pathway?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all meet qualification requirements' },
          { value: 'most', label: 'Most meet requirements, some pending' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-290: Home attendants must speak, read, write English and meet one qualification.',
      },
      {
        id: 'q2',
        question: 'Which qualification pathways do your home attendants use?',
        inputType: 'checklist',
        options: [
          { value: 'nursing_program', label: 'Completed nursing education program (RN/LPN) - 12VAC5-381-290(1)' },
          { value: 'nurse_aide_program', label: 'Completed Virginia Board of Nursing approved nurse aide program - 12VAC5-381-290(2)' },
          { value: 'cna_certified', label: 'Certified as nurse aide by Virginia Board of Nursing - 12VAC5-381-290(3)' },
          { value: 'nursing_student', label: 'Enrolled in nursing program with clinical experience - 12VAC5-381-290(4)' },
          { value: 'competency_eval', label: 'Passed competency evaluation per 42 CFR 484.36(b) - 12VAC5-381-290(5)' },
          { value: 'dmas_training', label: 'Completed DMAS Personal Care Aide Training Curriculum - 12VAC5-381-290(6)' },
        ],
        helpText: 'Per 12VAC5-381-290: Acceptable qualification pathways.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home attendants meet qualification requirements per 12VAC5-381-290.',
      atRisk: 'Some home attendants may not meet qualification requirements.',
      nonCompliant: 'Home attendants do not meet 12VAC5-381-290 qualification requirements.',
      unknown: 'Verify home attendant qualifications.',
    },
    stateNotes: [
      'Must speak, read, write English per 12VAC5-381-290',
      'Multiple qualification pathways available per 12VAC5-381-290(1-6)',
      'DMAS Personal Care Aide Training for personal care services only per 12VAC5-381-290(6)',
      'Competency evaluation must meet 42 CFR 484.36(b) criteria per 12VAC5-381-290(5)',
    ],
  },

  {
    id: 'VA-D2',
    itemId: 'D2',
    stateCode: 'VA',
    title: 'Annual In-Service Training (12VAC5-381-330(D) / 12VAC5-381-360(G))',
    description: 'Virginia requires 12 hours of annual in-service training for home attendants',
    severity: 'high',
    regulationCode: '12VAC5-381-330(D), 12VAC5-381-360(G)',
    regulationName: 'Annual In-Service Training',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home attendants complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-330(D) for skilled services, 12VAC5-381-360(G) for personal care: 12 hours annually.',
      },
      {
        id: 'q2',
        question: 'Is in-service training documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 12VAC5-381-200(O)(7): Training records in personnel file.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets 12-hour requirement per 12VAC5-381-330(D) and 12VAC5-381-360(G).',
      atRisk: 'Some home attendants are behind on annual training. Complete training immediately.',
      nonCompliant: 'No structured annual training program. Virginia requires 12 hours annually.',
      unknown: 'Review annual training program.',
    },
    stateNotes: [
      '12 hours annually per 12VAC5-381-330(D) for skilled services',
      '12 hours annually per 12VAC5-381-360(G) for personal care services',
      'In-service may be in conjunction with on-site supervision',
      'Training documented in personnel file per 12VAC5-381-200(O)(7)',
    ],
  },

  {
    id: 'VA-D3',
    itemId: 'D3',
    stateCode: 'VA',
    title: 'Orientation Training (12VAC5-381-200(I))',
    description: 'Virginia requires comprehensive orientation for all employees',
    severity: 'high',
    regulationCode: '12VAC5-381-200(I)',
    regulationName: 'Orientation',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does orientation include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'mission', label: 'Organization mission and goals - 12VAC5-381-200(I)(1)' },
          { value: 'structure', label: 'Organization structure - 12VAC5-381-200(I)(2)' },
          { value: 'policies', label: 'Relevant policies and procedures - 12VAC5-381-200(I)(3)' },
          { value: 'job_duties', label: 'Job duties and responsibilities - 12VAC5-381-200(I)(4)' },
          { value: 'client_rights', label: 'Client rights - 12VAC5-381-200(I)(5)' },
          { value: 'confidentiality', label: 'Confidentiality requirements - 12VAC5-381-200(I)(6)' },
          { value: 'emergency', label: 'Emergency procedures - 12VAC5-381-200(I)(7)' },
          { value: 'infection_control', label: 'Infection control - 12VAC5-381-200(I)(8)' },
          { value: 'regulations', label: 'Applicable laws and regulations - 12VAC5-381-200(I)(9)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-200(I): Orientation shall include these elements.',
      },
      {
        id: 'q2',
        question: 'Is orientation completed BEFORE employees provide client services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before first client' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 8 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training meets 12VAC5-381-200(I) requirements.',
      atRisk: 'Orientation may be missing required elements.',
      nonCompliant: 'Orientation is inadequate per 12VAC5-381-200(I).',
      unknown: 'Review orientation program.',
    },
    stateNotes: [
      'Orientation commensurate with job responsibilities per 12VAC5-381-200(I)',
      'Must include infection control per 12VAC5-381-200(I)(8)',
      'Must include client rights per 12VAC5-381-200(I)(5)',
      'Documentation in personnel file per 12VAC5-381-200(O)(6)',
    ],
  },

  // ============================================
  // SECTION E: HEALTH & SAFETY
  // ============================================
  {
    id: 'VA-E1',
    itemId: 'E1',
    stateCode: 'VA',
    title: 'Infection Control Program (12VAC5-381-260)',
    description: 'Virginia requires comprehensive infection control policies',
    severity: 'high',
    regulationCode: '12VAC5-381-260',
    regulationName: 'Infection Control',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'written_program', label: 'Written infection control program - 12VAC5-381-260(A)' },
          { value: 'surveillance', label: 'Surveillance of infections - 12VAC5-381-260(B)' },
          { value: 'prevention', label: 'Prevention and control measures - 12VAC5-381-260(C)' },
          { value: 'training', label: 'Staff training on infection control - 12VAC5-381-200(I)(8)' },
          { value: 'ppe', label: 'Personal protective equipment availability' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-260: Infection control requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 12VAC5-381-260 requirements.',
      atRisk: 'Infection control program may be incomplete.',
      nonCompliant: 'Infection control program is inadequate per 12VAC5-381-260.',
      unknown: 'Review infection control program.',
    },
    stateNotes: [
      'Written infection control program per 12VAC5-381-260(A)',
      'Surveillance and prevention measures per 12VAC5-381-260(B-C)',
      'Staff training on infection control per 12VAC5-381-200(I)(8)',
    ],
  },

  {
    id: 'VA-E2',
    itemId: 'E2',
    stateCode: 'VA',
    title: 'Mandated Reporter Training (Code of Virginia § 63.2-1606)',
    description: 'All direct care staff are mandated reporters of abuse and neglect',
    severity: 'high',
    regulationCode: 'Code of Virginia § 63.2-1606',
    regulationName: 'Adult Protective Services - Mandated Reporters',
    agencyName: 'Virginia Department of Social Services',
    agencyAbbr: 'VDSS',
    agencyPhone: '(888) 832-3858',
    agencyUrl: 'https://www.dss.virginia.gov/family/as/aps.cgi',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Per Code of Virginia § 63.2-1606: Direct care staff are mandated reporters.',
      },
      {
        id: 'q2',
        question: 'When do staff receive mandated reporter training?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'During orientation and annually' },
          { value: 'orientation_only', label: 'During orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff trained on mandated reporter duties per Code of Virginia § 63.2-1606.',
      atRisk: 'Some staff may lack mandated reporter training. Complete training for all.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates Code of Virginia § 63.2-1606.',
      unknown: 'Verify mandated reporter training.',
    },
    stateNotes: [
      'APS Hotline: (888) 832-3858',
      'Immediate reporting required for suspected abuse/neglect',
      'Failure to report may result in civil penalties',
      'Abuse/neglect reporting policy required per 12VAC5-381-180(A)(9)',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'VA-F1',
    itemId: 'F1',
    stateCode: 'VA',
    title: 'Personal Care Services Plan (12VAC5-381-360)',
    description: 'Virginia requires written plan of services developed by RN for personal care',
    severity: 'high',
    regulationCode: '12VAC5-381-360(C)',
    regulationName: 'Personal Care Services',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is a written plan of services developed by an RN for EVERY personal care client?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before services begin' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-360(C): Written plan developed by RN in collaboration with client.',
      },
      {
        id: 'q2',
        question: 'Is the plan reviewed with the assigned home attendant before delivering services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 12VAC5-381-360(D): Plan reviewed with assigned home attendant before delivering services.',
      },
      {
        id: 'q3',
        question: 'Is a copy of the plan provided to the client?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 12VAC5-381-360(D): Copy provided to client receiving services.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personal care service plans meet 12VAC5-381-360(C-D) requirements.',
      atRisk: 'Service plans may not be consistently completed before services begin.',
      nonCompliant: 'Services provided without required RN-developed plan per 12VAC5-381-360(C).',
      unknown: 'Review care plan process.',
    },
    stateNotes: [
      'Written plan developed by RN per 12VAC5-381-360(C)',
      'Plan developed in collaboration with client and family per 12VAC5-381-360(C)',
      'Plan retained in client record per 12VAC5-381-360(D)',
      'Copy provided to client per 12VAC5-381-360(D)',
      'Plan reviewed with home attendant before services per 12VAC5-381-360(D)',
    ],
  },

  {
    id: 'VA-F2',
    itemId: 'F2',
    stateCode: 'VA',
    title: 'Supervision of Personal Care Services (12VAC5-381-360(E-F))',
    description: 'Virginia requires RN supervision not to exceed 90 days',
    severity: 'high',
    regulationCode: '12VAC5-381-360(E-F)',
    regulationName: 'Supervision of Personal Care Services',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often is supervision provided for personal care services?',
        inputType: 'select',
        options: [
          { value: 'every_90', label: 'Every 90 days or more frequently (meets requirement)' },
          { value: 'every_120', label: 'Every 120 days' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No formal supervision schedule' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-360(E): Supervision not to exceed 90 days.',
      },
      {
        id: 'q2',
        question: 'Is an RN or LPN available during all hours personal care services are provided?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-360(F): RN or LPN available during all personal care service hours.',
      },
      {
        id: 'q3',
        question: 'Is supervision documented?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / inconsistently' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no formal schedule, no RN available, OR not documented
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['annually', 'never'] },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: meets all requirements
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_90' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: frequency slightly off (every 120 days) but otherwise compliant
      { condition: 'equals', questionId: 'q1', value: 'every_120', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets 12VAC5-381-360(E-F) requirements (90-day max, RN/LPN available).',
      atRisk: 'Supervision frequency or documentation needs improvement.',
      nonCompliant: 'Supervision does not meet 12VAC5-381-360(E-F) requirements.',
      unknown: 'Review supervision practices.',
    },
    stateNotes: [
      'Supervision not to exceed 90 days per 12VAC5-381-360(E)',
      'RN or LPN available during all service hours per 12VAC5-381-360(F)',
      'Supervision based on client needs and RN assessment per 12VAC5-381-360(E)',
    ],
  },

  // ============================================
  // SECTION G: CLIENT RECORDS
  // ============================================
  {
    id: 'VA-G1',
    itemId: 'G1',
    stateCode: 'VA',
    title: 'Client Record System (12VAC5-381-280)',
    description: 'Virginia requires organized client record system with specific contents',
    severity: 'high',
    regulationCode: '12VAC5-381-280',
    regulationName: 'Client Record System',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do client records include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'identification', label: 'Client identification information - 12VAC5-381-280(F)(1)' },
          { value: 'emergency_contact', label: 'Emergency contact information - 12VAC5-381-280(F)(2)' },
          { value: 'physician', label: 'Physician information - 12VAC5-381-280(F)(3)' },
          { value: 'assessment', label: 'Assessment documentation - 12VAC5-381-280(F)(4)' },
          { value: 'care_plan', label: 'Plan of care/service plan - 12VAC5-381-280(F)(5)' },
          { value: 'service_notes', label: 'Signed/dated notes on services provided - 12VAC5-381-280(G)' },
          { value: 'authorizations', label: 'Service authorizations - 12VAC5-381-280(F)(6)' },
          { value: 'discharge', label: 'Discharge summary - 12VAC5-381-280(F)(7)' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-280(F): Required client record contents.',
      },
      {
        id: 'q2',
        question: 'Are service notes written on the day service is delivered?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Yes, same day' },
          { value: 'next_day', label: 'Within 24 hours' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'delayed', label: 'Often delayed' },
        ],
        helpText: 'Per 12VAC5-381-280(G): Notes written on day service delivered, incorporated within 7 working days.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'same_day' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'next_day' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records meet 12VAC5-381-280 requirements.',
      atRisk: 'Client records may be missing elements or documentation delayed.',
      nonCompliant: 'Client records are inadequate per 12VAC5-381-280.',
      unknown: 'Audit client records.',
    },
    stateNotes: [
      'Organized record system per 12VAC5-381-280(A)',
      'Records safeguarded against loss per 12VAC5-381-280(B)',
      'Confidentiality maintained per 12VAC5-381-280(C)',
      'Notes written on day of service per 12VAC5-381-280(G)',
      'Notes incorporated within 7 working days per 12VAC5-381-280(G)',
    ],
  },

  {
    id: 'VA-G2',
    itemId: 'G2',
    stateCode: 'VA',
    title: 'Client Record Confidentiality (12VAC5-381-280(C))',
    description: 'Virginia requires client records to be confidential',
    severity: 'high',
    regulationCode: '12VAC5-381-280(C)',
    regulationName: 'Client Record Confidentiality',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have confidentiality policies and procedures?',
        inputType: 'checklist',
        options: [
          { value: 'written_policy', label: 'Written confidentiality policy' },
          { value: 'access_control', label: 'Only authorized personnel have access - 12VAC5-381-280(C)' },
          { value: 'hipaa', label: 'HIPAA compliance procedures' },
          { value: 'staff_training', label: 'Staff trained on confidentiality - 12VAC5-381-200(I)(6)' },
          { value: 'signed_agreements', label: 'Staff sign confidentiality agreements' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-280(C): Records confidential, only authorized access.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client record confidentiality meets requirements.',
      atRisk: 'Confidentiality policies may need strengthening.',
      nonCompliant: 'Confidentiality policies are inadequate.',
      unknown: 'Review confidentiality procedures.',
    },
    stateNotes: [
      'Records confidential per 12VAC5-381-280(C)',
      'Only authorized personnel access per state and federal law',
      'Staff trained on confidentiality per 12VAC5-381-200(I)(6)',
    ],
  },

  {
    id: 'VA-G3',
    itemId: 'G3',
    stateCode: 'VA',
    title: 'Record Retention and Protection (12VAC5-381-280(D-E))',
    description: 'Virginia requires safe storage and retention arrangements for client records',
    severity: 'medium',
    regulationCode: '12VAC5-381-280(D-E)',
    regulationName: 'Record Retention and Protection',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have provisions for safe storage and accurate reproduction of records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 12VAC5-381-280(D): Safe storage of original and accurate reproductions.',
      },
      {
        id: 'q2',
        question: 'Do you have policies for record retention and protection if organization discontinues operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 12VAC5-381-280(E): Arrangements for retention if discontinuing operation.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention and protection meets 12VAC5-381-280(D-E) requirements.',
      atRisk: 'Record protection policies may be incomplete.',
      nonCompliant: 'Record retention and protection is inadequate per 12VAC5-381-280(D-E).',
      unknown: 'Review record retention policies.',
    },
    stateNotes: [
      'Safe storage and accurate reproductions per 12VAC5-381-280(D)',
      'Retention arrangements if discontinuing operation per 12VAC5-381-280(E)',
      'Notification to OLC and client of record location per 12VAC5-381-280(E)',
    ],
  },

  // ============================================
  // SECTION H: SKILLED SERVICES (if applicable)
  // ============================================
  {
    id: 'VA-H1',
    itemId: 'H1',
    stateCode: 'VA',
    title: 'Medical Plan of Care (12VAC5-381-300)',
    description: 'For skilled services, Virginia requires a medical plan of care prescribed by physician',
    severity: 'high',
    regulationCode: '12VAC5-381-300(B)',
    regulationName: 'Skilled Services - Medical Plan of Care',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide skilled services (nursing, therapy)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - personal care only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For skilled services, is there a medical plan of care prescribed by the physician?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before services begin' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 12VAC5-381-300(B): Medical plan of care approved and signed by physician.',
      },
      {
        id: 'q3',
        question: 'Is the medical plan of care reviewed as often as the client\'s condition requires?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 12VAC5-381-300(D): Plan reviewed as often as client condition requires.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical plan of care meets 12VAC5-381-300 requirements.',
      atRisk: 'Medical plans of care may not be consistently completed.',
      nonCompliant: 'Skilled services provided without required medical plan of care per 12VAC5-381-300.',
      unknown: 'Review medical plan of care process.',
    },
    stateNotes: [
      'Medical plan of care signed by physician per 12VAC5-381-300(B)',
      'Verbal orders documented within 24 hours per 12VAC5-381-300(C)',
      'Plan reviewed as often as condition requires per 12VAC5-381-300(D)',
      'Applies to Part III skilled services only',
    ],
  },

  {
    id: 'VA-H2',
    itemId: 'H2',
    stateCode: 'VA',
    title: 'Supervision of Skilled Services (12VAC5-381-310)',
    description: 'For skilled services, Virginia requires supervision not to exceed 90 days',
    severity: 'high',
    regulationCode: '12VAC5-381-310(B), 12VAC5-381-330(B)',
    regulationName: 'Skilled Services - Supervision',
    agencyName: 'Virginia Department of Health',
    agencyAbbr: 'VDH',
    agencyPhone: '(804) 367-2106',
    agencyUrl: 'https://www.vdh.virginia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide skilled services (nursing, therapy)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - personal care only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is supervision of home attendants assisting with skilled services provided at least every 90 days?',
        inputType: 'select',
        options: [
          { value: 'every_90', label: 'Every 90 days or more frequently' },
          { value: 'less_frequent', label: 'Less frequently than 90 days' },
          { value: 'no', label: 'No formal supervision schedule' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 12VAC5-381-330(B): Supervision not to exceed 90 days.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_90', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'less_frequent', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skilled services supervision meets 90-day requirement per 12VAC5-381-330(B).',
      atRisk: 'Supervision intervals may exceed 90-day maximum.',
      nonCompliant: 'No formal supervision schedule for skilled services per 12VAC5-381-330(B).',
      unknown: 'Review skilled services supervision.',
    },
    stateNotes: [
      'Supervision not to exceed 90 days per 12VAC5-381-330(B)',
      'Home attendants work under supervision of appropriate health care professional per 12VAC5-381-330(C)',
      '12 hours annual in-service for home attendants assisting skilled services per 12VAC5-381-330(D)',
    ],
  },
];

export default VIRGINIA_QUESTIONS;